import React, { useState, useEffect, useRef } from 'react';

/**
 * TabView - A component that manages multiple tabs/views
 * 
 * @param {Object} props
 * @param {Array} props.tabs - Array of tab objects with {id, title, content, icon, closable}
 * @param {function} props.onTabChange - Callback when active tab changes
 * @param {function} props.onTabClose - Callback when a tab is closed
 * @param {function} props.onTabAdd - Callback when a new tab is requested
 * @param {string} props.activeTabId - ID of the currently active tab
 * @param {boolean} props.canAddTabs - Whether new tabs can be added
 * @param {boolean} props.fillHeight - Whether the tab content should fill available height
 */
const TabView = ({ 
  tabs = [],
  onTabChange,
  onTabClose,
  onTabAdd,
  activeTabId,
  canAddTabs = true,
  fillHeight = true,
}) => {
  const [activeTab, setActiveTab] = useState(activeTabId || (tabs[0]?.id));
  const [draggedTab, setDraggedTab] = useState(null);
  const [dragOverTab, setDragOverTab] = useState(null);
  const tabsRef = useRef({});
  const tabBarRef = useRef(null);
  
  // Update active tab if activeTabId prop changes
  useEffect(() => {
    if (activeTabId && activeTabId !== activeTab) {
      setActiveTab(activeTabId);
    }
  }, [activeTabId]);

  // Update active tab if current active tab is removed
  useEffect(() => {
    if (activeTab && !tabs.find(tab => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id);
    }
  }, [tabs]);

  // Handle tab selection
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  // Handle tab close
  const handleTabClose = (e, tabId) => {
    e.stopPropagation();
    if (onTabClose) {
      onTabClose(tabId);
    }
  };

  // Handle tab add
  const handleAddTab = () => {
    if (onTabAdd) {
      onTabAdd();
    }
  };

  // Drag and drop handlers for tab reordering
  const handleDragStart = (e, tabId) => {
    const tabObj = tabs.find(tab => tab.id === tabId);
    setDraggedTab(tabId);
    e.dataTransfer.effectAllowed = 'move';

    // Strip React elements and non-serializable content before storing
    const serializableTabData = {
      id: tabObj.id,
      title: tabObj.title,
      type: tabObj.type,
      props: tabObj.props || {},
      closable: tabObj.closable
      // Don't include content as it's a React element and can't be serialized
    };

    // Store both the tab ID and the serializable tab data
    e.dataTransfer.setData('application/json', JSON.stringify(serializableTabData));
    e.dataTransfer.setData('text/plain', tabId);

    // Add a custom class name to identify the source panel
    const panelElement = e.currentTarget.closest('.left-panel, .right-panel');
    if (panelElement) {
      const isPanelLeft = panelElement.classList.contains('left-panel');
      e.dataTransfer.setData('panel-source', isPanelLeft ? 'left' : 'right');
    }

    // Add semi-transparent effect
    if (e.target) {
      setTimeout(() => {
        e.target.style.opacity = '0.5';
      }, 0);
    }

    // Dispatch a custom event that can be caught by DatabaseViewer
    const dragStartEvent = new CustomEvent('tab-drag-start', {
      bubbles: true,
      detail: { tabId, tabData: serializableTabData }
    });
    e.target.dispatchEvent(dragStartEvent);
  };

  const handleDragEnd = (e) => {
    setDraggedTab(null);
    setDragOverTab(null);

    // Reset opacity
    if (e.target) {
      e.target.style.opacity = '1';
    }

    // Dispatch a custom event that can be caught by DatabaseViewer
    const dragEndEvent = new CustomEvent('tab-drag-end', {
      bubbles: true,
      detail: {}
    });
    e.target.dispatchEvent(dragEndEvent);
  };

  const handleDragOver = (e, tabId) => {
    e.preventDefault();
    if (draggedTab === tabId) return;
    setDragOverTab(tabId);

    // Set the drop effect
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetTabId) => {
    e.preventDefault();
    if (draggedTab === targetTabId) return;

    try {
      // Get the source panel
      const panelSource = e.dataTransfer.getData('panel-source');

      // Get the current panel
      const panelElement = e.currentTarget.closest('.left-panel, .right-panel');
      const isPanelLeft = panelElement && panelElement.classList.contains('left-panel');
      const currentPanel = isPanelLeft ? 'left' : 'right';

      // Check if this is a cross-panel drop (tab moving between panels)
      const isCrossPanelDrop = panelSource && panelSource !== currentPanel;

      if (isCrossPanelDrop) {
        // For cross-panel drops, dispatch a custom event that can be caught by DatabaseViewer
        const tabData = JSON.parse(e.dataTransfer.getData('application/json'));
        const dropEvent = new CustomEvent('tab-cross-panel-drop', {
          bubbles: true,
          detail: {
            tabId: draggedTab,
            tabData,
            sourcePanel: panelSource,
            targetPanel: currentPanel,
            targetTabId
          }
        });
        e.currentTarget.dispatchEvent(dropEvent);
      } else {
        // For same-panel drops, reorder tabs within the same panel
        const draggedIndex = tabs.findIndex(tab => tab.id === draggedTab);
        const targetIndex = tabs.findIndex(tab => tab.id === targetTabId);

        if (draggedIndex !== -1 && targetIndex !== -1) {
          // Create a reordered tabs array
          const reorderedTabs = [...tabs];
          const [draggedTabObj] = reorderedTabs.splice(draggedIndex, 1);
          reorderedTabs.splice(targetIndex, 0, draggedTabObj);

          // Notify parent component of the reordering
          if (onTabChange) {
            onTabChange(activeTab, reorderedTabs);
          }
        }
      }
    } catch (error) {
      console.error('Error during tab drop operation:', error);
    }

    setDraggedTab(null);
    setDragOverTab(null);
  };

  // Scroll tab bar horizontally when tabs overflow
  const handleTabBarScroll = (direction) => {
    if (tabBarRef.current) {
      const scrollAmount = 200; // pixels to scroll
      tabBarRef.current.scrollLeft += direction * scrollAmount;
    }
  };

  // Calculate the total width of all tabs to determine if scrolling is needed
  const calculateTabsWidth = () => {
    if (!tabBarRef.current) return 0;
    
    let totalWidth = 0;
    Object.values(tabsRef.current).forEach(tabEl => {
      if (tabEl) {
        totalWidth += tabEl.offsetWidth;
      }
    });
    
    return totalWidth;
  };

  const [showScrollButtons, setShowScrollButtons] = useState(false);

  // Make tab container droppable for tabs from other panels
  useEffect(() => {
    const tabBar = tabBarRef.current;
    if (!tabBar) return;

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e) => {
      e.preventDefault();

      try {
        const tabDataStr = e.dataTransfer.getData('application/json');
        const panelSource = e.dataTransfer.getData('panel-source');
        const panelElement = e.currentTarget.closest('.left-panel, .right-panel');

        if (panelElement && tabDataStr) {
          const isPanelLeft = panelElement.classList.contains('left-panel');
          const currentPanel = isPanelLeft ? 'left' : 'right';

          // If this is a cross-panel drop
          if (panelSource && panelSource !== currentPanel) {
            // Parse the serialized tab data
            const tabData = JSON.parse(tabDataStr);

            // Dispatch custom event
            const dropEvent = new CustomEvent('tab-cross-panel-drop', {
              bubbles: true,
              detail: {
                tabData: tabData,
                sourcePanel: panelSource,
                targetPanel: currentPanel
              }
            });
            tabBar.dispatchEvent(dropEvent);
          }
        }
      } catch (error) {
        console.error('Error handling tab drop:', error);
      }
    };

    tabBar.addEventListener('dragover', handleDragOver);
    tabBar.addEventListener('drop', handleDrop);

    return () => {
      tabBar.removeEventListener('dragover', handleDragOver);
      tabBar.removeEventListener('drop', handleDrop);
    };
  }, []);

  // Check if tab bar needs scroll buttons
  useEffect(() => {
    const checkScrollable = () => {
      if (tabBarRef.current) {
        const totalTabsWidth = calculateTabsWidth();
        const tabBarWidth = tabBarRef.current.clientWidth;
        setShowScrollButtons(totalTabsWidth > tabBarWidth);
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);

    return () => {
      window.removeEventListener('resize', checkScrollable);
    };
  }, [tabs]);

  // Find the active tab object
  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="flex flex-col h-full">
      {/* Tab Bar */}
      <div className="flex items-center border-b border-gray-200 bg-gray-50">
        {/* Left scroll button */}
        {showScrollButtons && (
          <button 
            onClick={() => handleTabBarScroll(-1)}
            className="flex-shrink-0 px-2 py-3 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        
        {/* Scrollable Tabs */}
        <div 
          ref={tabBarRef}
          className="flex overflow-x-auto hide-scrollbar flex-grow" 
          style={{ scrollBehavior: 'smooth' }}
        >
          {tabs.map((tab) => (
            <div
              key={tab.id}
              ref={el => tabsRef.current[tab.id] = el}
              className={`
                flex items-center px-4 py-3 text-sm font-medium border-r border-gray-200
                ${activeTab === tab.id
                  ? 'text-indigo-600 border-b-2 border-b-indigo-500 bg-white'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'}
                ${dragOverTab === tab.id ? 'border-l-2 border-l-indigo-500' : ''}
                cursor-pointer select-none
              `}
              onClick={() => handleTabClick(tab.id)}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, tab.id)}
              onDrop={(e) => handleDrop(e, tab.id)}
            >
              {tab.icon && (
                <span className="mr-2">{tab.icon}</span>
              )}
              <span>{tab.title}</span>
              {tab.closable && (
                <>
                  <button
                    onClick={(e) => handleTabClose(e, tab.id)}
                    className="ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  {/* Split icon removed */}
                </>
              )}
            </div>
          ))}
        </div>

        {/* Right scroll button */}
        {showScrollButtons && (
          <button 
            onClick={() => handleTabBarScroll(1)}
            className="flex-shrink-0 px-2 py-3 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        
        {/* Add new tab button */}
        {canAddTabs && (
          <button
            onClick={handleAddTab}
            className="flex-shrink-0 px-3 py-3 text-gray-500 hover:text-gray-700 focus:outline-none"
            title="Add new tab"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className={`flex-grow bg-white overflow-auto ${fillHeight ? 'h-full' : ''}`}>
        {activeTabContent}
      </div>
    </div>
  );
};

// Add custom styles to hide scrollbar but allow scrolling
const style = document.createElement('style');
style.textContent = `
  .hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;     /* Firefox */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;             /* Chrome, Safari, Opera */
  }
`;
document.head.appendChild(style);

export default TabView;